import React, { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../store/hook";
import { fetchTenantVpaMappings, updateVpaStatus, VpaDetails, TenantVpaMapping, Tenant } from "../../features/outlet/TenantSlice";
import { generateUtrLink, clearUtr, createUtr } from "../../features/outlet/UtrSlice";
import { RootState } from "../../store/store";
import { Plus, AlertCircle, Loader2, CheckCircle, CalendarDays } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomModal from "../common/CustomModal";

/* --------------------------------- Interfaces ------------------------------ */

interface VpaListingProps {
  tenantVpaMappings: TenantVpaMapping[];
  tenant: Tenant;
  tenantLoading: boolean;
  tenantError: any;
  onBack: () => void;
}

export interface UtrForm {
  [key: string]: {
    utrError?: boolean;
    dateError?: boolean;
    utr?: string;
    date?: Date | null;
  };
}

const VpaListing: React.FC<VpaListingProps> = ({ tenant, tenantError, tenantLoading, tenantVpaMappings, onBack }) => {
  /* --------------------------------- Redux ------------------------------ */

  const dispatch = useAppDispatch();
  const { link, utrError, utrLoading } = useAppSelector((state: RootState) => state.utr);

  /* --------------------------------- States ------------------------------ */

  const [updatingVpaId, setUpdatingVpaId] = useState<number | null>(null);
  const [utrForm, setUtrForm] = useState<UtrForm>({});
  const [showQrModal, setShowQrModal] = useState(false);
  const [selectedQrImage, setSelectedQrImage] = useState<string>("");

  /* ------------------------------ Event Handlers ---------------------------- */
  useEffect(() => {
    if (utrError || link) {
      setTimeout(() => {
        dispatch(clearUtr());
      }, 3000);
    }
  }, [dispatch, utrError, link]);

  const handleQrImageClick = (qrImage: string) => {
    setSelectedQrImage(qrImage);
    setShowQrModal(true);
  };

  /* ------------------------------ Event Handlers ---------------------------- */

  const handleUpdateVpaStatus = async (vpaId: number, currentStatus: string) => {
    setUpdatingVpaId(vpaId);
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await dispatch(updateVpaStatus({ vpaId: vpaId, status: newStatus }) as any);
      dispatch(fetchTenantVpaMappings(tenant.tenantId) as any);
    } catch (error) {
      console.error("Failed to update VPA status:", error);
    }
    setUpdatingVpaId(null);
  };

  const handleAddUtr = async (vpa: VpaDetails, utr: string, tenantId: number, date: Date | null) => {

    const vpaId = vpa.vpaId;
    setUpdatingVpaId(vpaId);
    let hasDateError = false;
    let hasUtrError = false;


    if (!utrForm[vpaId]?.date) {
      hasDateError = true
    }

    if (!utrForm[vpaId]?.utr) {
      hasUtrError = true
    }


    setUtrForm((prevState) => {
      return {
        ...prevState,
        [vpaId]: {
          ...prevState[vpaId],
          utrError: hasUtrError,
          dateError: hasDateError
        },
      };
    });

    if (hasDateError || hasUtrError) {
      setUpdatingVpaId(null);
      return;
    }

    if (!utr || !date) {
      setUpdatingVpaId(null);
      return;
    }

    await dispatch(
      createUtr({
        utr,
        vpaId: vpa.vpaId,
        tenantId: tenantId,
        transactionDate: date
      }),
    );

    setUpdatingVpaId(null);
    dispatch(fetchTenantVpaMappings(tenantId) as any);
  };

  const handleGenerateLink = async (vpa: VpaDetails, tenantId: number) => {
    setUpdatingVpaId(vpa.vpaId);
    await dispatch(
      generateUtrLink({
        vpaId: vpa.vpaId,
        tenantId: tenantId,
      }),
    );
    setUpdatingVpaId(null);
  };

  /* ------------------------------ UI Handlers ---------------------------- */

  const getUtrContent = (vpa: VpaDetails, tenantId: number) => {
    const utr = utrForm[vpa["vpaId"]]?.utr || "";
    const date = utrForm[vpa["vpaId"]]?.date || null;
    const utrError = utrForm[vpa["vpaId"]]?.utrError ? true : false;
    const dateError = utrForm[vpa["vpaId"]]?.dateError ? true : false;

    if (vpa.hasConsent) {
      return (
        <>
          <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100">
            Verified
          </button>
          <div className="min-w-[250px]"></div>
        </>
      );
    }

    if (vpa.hasUtr) {
      return (
        <>
          <button
            disabled={!vpa.hasUtr}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand bg-brand/10 rounded-md hover:bg-brand/20 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={async (e) => {
              e.preventDefault();
              handleGenerateLink(vpa, tenantId);
            }}
          >
            Generate Link
          </button>
          <div className="min-w-[250px]"></div>
        </>
      );
    }

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const utr = (e.currentTarget.utr as HTMLInputElement).value;
          handleAddUtr(vpa, utr, tenantId, date);
        }}
        className="flex flex-col sm:flex-row sm:items-start gap-2 min-w-[250px]"
      >
        <div className="w-full sm:w-[170px]">
          <input
            type="text"
            name="utr"
            placeholder="Add UTR"
            value={utr}
            className={`w-full px-3 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors ${
              utrError ? "border-red-400 bg-red-50/30" : "border-cardbox"
            }`}
            onChange={(e) => {
              const value = e.target.value;
              const vpaId = vpa.vpaId;
              setUtrForm((prevState) => {
                return {
                  ...prevState,
                  [vpaId]: {
                    ...prevState[vpaId],
                    utr: value,
                    utrError: false,
                  },
                };
              });
            }}
          />
          {utrError && (
            <div className="mt-1 text-xs text-red-500">
              UTR is required
            </div>
          )}
        </div>

        <div className="relative w-full sm:w-[150px]">
          <DatePicker
            id="date"
            selected={date}
            onChange={(date: Date | null) => {
              setUtrForm((prevState) => {
                const vpaId = vpa.vpaId;
                return {
                  ...prevState,
                  [vpaId]: {
                    ...prevState[vpaId],
                    date: date,
                    dateError: false,
                  },
                };
              });
            }}
            placeholderText="Date"
            dateFormat="dd MMM yyyy"
            showPopperArrow={false}
            popperPlacement="bottom-end"
            wrapperClassName="w-full"
            calendarClassName="!border !border-gray-200 !rounded-lg !shadow-xl !font-sans"
            className={`w-full h-10 pl-3 pr-9 border rounded-md text-sm font-medium text-gray-700 bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors ${
              dateError ? "border-red-400 bg-red-50/30" : "border-cardbox"
            }`}
          />
          <CalendarDays className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          {dateError && (
            <div className="mt-1 text-xs text-red-500">
              Date is required
            </div>
          )}
        </div>

        <button
          type="submit"
          className="inline-flex items-center justify-center min-w-10 h-10 px-3 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-brand/50 disabled:opacity-60 disabled:cursor-not-allowed"
          disabled={utrLoading && vpa.vpaId === updatingVpaId}
        >
          {utrLoading && vpa.vpaId === updatingVpaId ? (
            <Loader2 className="w-5 h-5 animate-spin text-white" />
          ) : (
            <Plus className="w-5 h-5" />
          )}
        </button>
      </form>
    );
  };

  const totalVpas =
    tenantVpaMappings?.reduce((count, mapping) => count + (mapping.vpa?.length || 0), 0) ?? 0;

  const hasNoVpas =
    !tenantLoading &&
    !tenantError &&
    (totalVpas === 0 ||
      !tenantVpaMappings ||
      tenantVpaMappings.every((mapping) => !mapping.vpa || mapping.vpa.length === 0));

  return (
    <div className="space-y-6 px-8">
      <div className="space-y-4">
        {tenantVpaMappings?.map((mapping) =>
          mapping.vpa.map((vpa) => (
            <div
              key={vpa.vpaId}
              className="bg-white rounded-lg border border-cardborder shadow-sm hover:shadow-md transition-shadow duration-150"
            >
              <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
                {/* Left: QR + VPA info */}
                <div className="flex items-center gap-4 min-w-[240px] md:min-w-[260px]">
                  <div className="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden shadow-sm border border-gray-100">
                    {vpa.qrImage ? (
                      <img
                        src={vpa.qrImage}
                        alt="QR Code"
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => handleQrImageClick(vpa.qrImage || "")}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-lg">
                        <i className="bi bi-building-fill" />
                      </div>
                    )}
                  </div>
                  <div className="max-w-[260px]">
                    <h6 className="text-sm font-semibold text-gray-900 mb-0.5 break-all">
                      {vpa.vpa}
                    </h6>
                    {vpa.merchantNameFromVpa && (
                      <p className="text-[11px] text-gray-500 line-clamp-1">
                        {vpa.merchantNameFromVpa}
                      </p>
                    )}
                    <p className="mt-1 text-[11px] text-gray-400">
                      Added on{" "}
                      {new Date(vpa.createdAt).toLocaleString(undefined, {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>

                {/* Middle + Right: meta & actions */}
                <div className="flex flex-wrap items-center md:justify-end gap-4 md:gap-6 flex-1">
                  <div className="flex flex-col items-start md:items-center border-r border-gray-200 pr-4 md:pr-6 min-w-[90px]">
                    <span className="text-[11px] text-gray-500">Provider</span>
                    <span className="text-xs font-semibold text-gray-800 mt-1 break-all text-left md:text-center">
                      {vpa.providerName.toUpperCase()}
                    </span>
                  </div>

                  <div className="flex flex-col items-start md:items-center border-r border-gray-200 pr-4 md:pr-6 min-w-[90px]">
                    <span className="text-[11px] text-gray-500">Status</span>
                    <span
                      className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${
                        vpa.status === "ACTIVE"
                          ? "bg-green-50 text-green-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {vpa.status}
                    </span>
                  </div>

                  <div className="flex flex-col items-start md:items-center border-r border-gray-200 pr-4 md:pr-6 min-w-[80px]">
                    <span className="text-[11px] text-gray-500">MCC</span>
                    <span className="text-xs font-semibold text-gray-800 mt-1">
                      {(vpa as any).mcc || "-"}
                    </span>
                  </div>

                  <div className="flex flex-col items-start md:items-center min-w-[110px]">
                    <span className="text-[11px] text-gray-500">Verification</span>
                    <span
                      className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        vpa.hasConsent
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {vpa.hasConsent ? "Verified" : "Pending"}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 ml-auto">
                    <button
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md ${
                        vpa.status === "ACTIVE"
                          ? "text-red-600 bg-red-50 hover:bg-red-100"
                          : "text-green-600 bg-green-50 hover:bg-green-100"
                      }`}
                      onClick={() => handleUpdateVpaStatus(vpa.vpaId, vpa.status)}
                    >
                      {vpa.status === "ACTIVE" ? "Deactivate" : "Activate"}
                    </button>

                    {getUtrContent(vpa, mapping.tenantId)}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}

        {hasNoVpas && (
          <div className="flex flex-col items-center justify-center py-16 bg-white border border-dashed border-gray-200 rounded-lg">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <AlertCircle className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700">No VPAs added yet</p>
            <p className="text-xs text-gray-500 mt-1">
              Once VPAs are created for this outlet, they will appear here.
            </p>
          </div>
        )}
      </div>

      {tenantLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="flex items-center gap-2">
            <Loader2 className="w-10 h-10 animate-spin text-brand" />
            <span className="text-sm text-gray-500">Loading...</span>
          </div>
        </div>
      )}

      {tenantError && (
        <div className="flex justify-center items-center py-12">
          <div className="flex items-center gap-2 text-red-500">
            <AlertCircle className="w-5 h-5" />
            <span>{tenantError || "Something went wrong!"}</span>
          </div>
        </div>
      )}

      {utrError && (
        <div className="fixed bottom-4 right-4 bg-red-50 text-red-600 px-4 py-2 rounded-md shadow-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>{utrError}</span>
        </div>
      )}

      {link && (
        <div className="fixed bottom-4 right-4 bg-green-50 text-green-600 px-4 py-2 rounded-md shadow-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          <span>Email Sent Successfully!{" "}
            <button
              onClick={() => window.open("https://verifytest.fincentive.co/" + link, "_blank")}
              className="underline hover:text-green-700"
            >
              Click Here
            </button>
          </span>
        </div>
      )}

      {/* Custom Modal for QR Code Preview */}
      <CustomModal
        show={showQrModal}
        onHide={() => setShowQrModal(false)}
        title="QR Code Preview"
        width="w-[550px]"
      >
        <div className="w-[400px] h-[400px] flex items-center justify-center">
          <img
            src={selectedQrImage}
            alt="QR Code"
            className="w-[400px] h-[400px] object-contain"
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default VpaListing;
